import { uid } from '@/shared/utils/dateHelpers';
import { CUSTOM_SCHEDULE_TYPES, NTH_LABELS, WEEKDAY_NAMES } from '@/shared/constants/config';

function parseDayOfMonth(str) {
  if (!str) return null;
  const m = String(str).match(/\d+/);
  if (!m) return null;
  const n = Number(m[0]);
  if (!n || n < 1 || n > 31) return null;
  return n;
}

// Returns { kind: 'iso', date: Date } for ISO YYYY-MM-DD strings (new, calendar-picked),
// { kind: 'dom', day: 1..31 } for legacy "15th"/"1st" strings, or null.
function parseDueDate(raw) {
  if (!raw) return null;
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const d = toDate(raw);
    return d ? { kind: 'iso', date: d } : null;
  }
  const day = parseDayOfMonth(raw);
  return day ? { kind: 'dom', day } : null;
}

function clampDay(year, month, day) {
  const last = new Date(year, month + 1, 0).getDate();
  return Math.min(day, last);
}

function toISO(d) {
  return d.toISOString().split('T')[0];
}

function toDate(iso) {
  // Treat bare dates as UTC to avoid timezone drift.
  if (!iso) return null;
  if (typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(iso)) return new Date(iso + 'T00:00:00Z');
  return new Date(iso);
}

// Find the date of the Nth occurrence of `dayOfWeek` (0=Sun..6=Sat) in `year/month`.
// `nth`: 1..4 for 1st..4th, -1 for the last weekday of the month.
function nthWeekdayOfMonth(year, month, nth, dayOfWeek) {
  if (nth === -1) {
    const last = new Date(Date.UTC(year, month + 1, 0));
    const offset = (last.getUTCDay() - dayOfWeek + 7) % 7;
    return new Date(Date.UTC(year, month, last.getUTCDate() - offset));
  }
  const first = new Date(Date.UTC(year, month, 1));
  const offset = (dayOfWeek - first.getUTCDay() + 7) % 7;
  const day = 1 + offset + (nth - 1) * 7;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  if (day > last) return null; // e.g. no 5th Thursday this month
  return new Date(Date.UTC(year, month, day));
}

function stepForwardStandard(prev, freq, day) {
  const d = new Date(prev);
  if (freq === 'Weekly') {
    d.setUTCDate(d.getUTCDate() + 7);
    return d;
  }
  if (freq === 'Fortnightly') {
    d.setUTCDate(d.getUTCDate() + 14);
    return d;
  }
  const stepMonths = freq === 'Quarterly' ? 3 : freq === 'Annual' ? 12 : 1;
  // Build target year/month manually to avoid JS month-overflow (e.g. Jan 31 + 1 month -> Mar 3).
  const absMonth = d.getUTCMonth() + stepMonths;
  const ny = d.getUTCFullYear() + Math.floor(absMonth / 12);
  const nm = ((absMonth % 12) + 12) % 12;
  const clampedDay = clampDay(ny, nm, day || d.getUTCDate());
  return new Date(Date.UTC(ny, nm, clampedDay));
}

function stepForwardCustom(prev, schedule) {
  if (!schedule) return null;
  if (schedule.type === CUSTOM_SCHEDULE_TYPES.EVERY_N_WEEKS) {
    const weeks = Math.max(1, Number(schedule.intervalWeeks) || 2);
    const d = new Date(prev);
    d.setUTCDate(d.getUTCDate() + 7 * weeks);
    return d;
  }
  if (schedule.type === CUSTOM_SCHEDULE_TYPES.NTH_WEEKDAY) {
    const nth = Number(schedule.nth);
    const dow = Number(schedule.dayOfWeek);
    // Advance to next month, then find same Nth weekday.
    let y = prev.getUTCFullYear();
    let m = prev.getUTCMonth() + 1;
    for (let i = 0; i < 24; i++) {
      if (m > 11) { m = 0; y += 1; }
      const d = nthWeekdayOfMonth(y, m, nth, dow);
      if (d) return d;
      m += 1;
    }
    return null;
  }
  return null;
}

function stepForward(prev, bill, day) {
  if (bill.frequency === 'Custom') return stepForwardCustom(prev, bill.customSchedule);
  return stepForwardStandard(prev, bill.frequency || 'Monthly', day);
}

function firstOccurrenceCustom(bill) {
  const schedule = bill.customSchedule;
  if (!schedule) return null;
  const anchorISO = schedule.anchorDate || (bill.createdAt ? toISO(toDate(bill.createdAt)) : null);
  const anchor = anchorISO ? toDate(anchorISO) : toDate(toISO(new Date()));

  if (schedule.type === CUSTOM_SCHEDULE_TYPES.EVERY_N_WEEKS) {
    const dow = Number(schedule.dayOfWeek);
    const d = new Date(anchor);
    const offset = (dow - d.getUTCDay() + 7) % 7;
    d.setUTCDate(d.getUTCDate() + offset);
    return d;
  }
  if (schedule.type === CUSTOM_SCHEDULE_TYPES.NTH_WEEKDAY) {
    const nth = Number(schedule.nth);
    const dow = Number(schedule.dayOfWeek);
    let y = anchor.getUTCFullYear();
    let m = anchor.getUTCMonth();
    for (let i = 0; i < 24; i++) {
      const candidate = nthWeekdayOfMonth(y, m, nth, dow);
      if (candidate && candidate >= anchor) return candidate;
      m += 1;
      if (m > 11) { m = 0; y += 1; }
    }
    return null;
  }
  return null;
}

function firstOccurrence(bill) {
  if (bill.frequency === 'Custom') return firstOccurrenceCustom(bill);

  const anchorISO = bill.createdAt ? toISO(toDate(bill.createdAt)) : null;
  const anchor = anchorISO ? toDate(anchorISO) : toDate(toISO(new Date()));
  const freq = bill.frequency || 'Monthly';
  const parsed = parseDueDate(bill.dueDate);

  if (freq === 'Weekly' || freq === 'Fortnightly') {
    // ISO dueDate = explicit anchor picked from calendar; otherwise fall back to createdAt.
    return (parsed && parsed.kind === 'iso') ? parsed.date : anchor;
  }

  // Monthly / Quarterly / Annual
  if (parsed && parsed.kind === 'iso') {
    return parsed.date;
  }

  const day = (parsed && parsed.kind === 'dom' ? parsed.day : null) || 1;
  const start = new Date(anchor);
  start.setUTCDate(clampDay(start.getUTCFullYear(), start.getUTCMonth(), day));
  if (start < anchor) {
    return stepForwardStandard(start, freq, day);
  }
  return start;
}

export function billOccurrenceKey(billId, dateISO) {
  return billId + '::' + dateISO;
}

// Derives the day-of-month to pass to stepForwardStandard for clamping.
// ISO dueDate → use its day; legacy dom → use that day; Custom/empty → null (stepForward ignores it).
function dueDateDay(bill) {
  const parsed = parseDueDate(bill.dueDate);
  if (!parsed) return null;
  if (parsed.kind === 'iso') return parsed.date.getUTCDate();
  return parsed.day;
}

export function computeOccurrences(bill, throughISO) {
  if (!bill) return [];
  if (!bill.category) return [];
  if (bill.autoGenerate === false) return [];

  const through = toDate(throughISO);
  if (!through) return [];

  const day = dueDateDay(bill);

  let current = bill.lastGeneratedDate
    ? stepForward(toDate(bill.lastGeneratedDate), bill, day)
    : firstOccurrence(bill);

  const out = [];
  let safety = 0;
  while (current && current <= through && safety < 600) {
    out.push(toISO(current));
    current = stepForward(current, bill, day);
    safety += 1;
  }
  return out;
}

// Enumerates every date this bill would be due within [fromISO, throughISO],
// regardless of whether matching transactions have been materialized.
// Used by the calendar view; distinct from computeOccurrences which drives tx generation.
export function occurrencesInRange(bill, fromISO, throughISO) {
  if (!bill) return [];
  if (!bill.category) return [];
  const from = toDate(fromISO);
  const through = toDate(throughISO);
  if (!from || !through || from > through) return [];

  const day = dueDateDay(bill);

  let current = firstOccurrence(bill);
  const out = [];
  let safety = 0;
  while (current && current <= through && safety < 600) {
    if (current >= from) out.push(toISO(current));
    current = stepForward(current, bill, day);
    safety += 1;
  }
  return out;
}

// Returns the next ISO date this bill is due on or after todayISO.
// Ignores lastGeneratedDate — purely calendar-driven.
// Returns null when the bill has no category or no future occurrences.
export function nextDueDate(bill, todayISO) {
  if (!bill || !bill.category) return null;
  const today = toDate(todayISO || toISO(new Date()));
  if (!today) return null;

  const day = dueDateDay(bill);
  let current = firstOccurrence(bill);
  let safety = 0;
  while (current && current < today && safety < 600) {
    current = stepForward(current, bill, day);
    safety += 1;
  }
  return current ? toISO(current) : null;
}

export function buildTxnFromOccurrence(bill, dateISO, { defaultPayment, resolvedUserName }) {
  return {
    id: uid(),
    date: dateISO,
    description: bill.name,
    category: bill.category,
    amount: Number(bill.amount) || 0,
    type: 'expense',
    paymentMethod: bill.autoPay ? 'Direct Debit' : (defaultPayment || 'Debit Card'),
    who: resolvedUserName || 'User',
    notes: '',
    sourceBillId: bill.id,
  };
}

export function materialize(bills, existingTxns, { today, defaultPayment, resolvedUserName }) {
  const existingKeys = new Set();
  for (const tx of existingTxns || []) {
    if (tx && tx.sourceBillId) existingKeys.add(tx.sourceBillId + '::' + tx.date);
  }

  const newTxns = [];
  const billUpdates = new Map();

  for (const bill of bills || []) {
    const occurrences = computeOccurrences(bill, today);
    if (occurrences.length === 0) continue;

    for (const date of occurrences) {
      const key = bill.id + '::' + date;
      if (existingKeys.has(key)) continue;
      newTxns.push(buildTxnFromOccurrence(bill, date, { defaultPayment, resolvedUserName }));
    }
    billUpdates.set(bill.id, occurrences[occurrences.length - 1]);
  }

  return { newTxns, billUpdates };
}

// Human-readable label for a bill's schedule. Used in list/calendar UI.
export function formatSchedule(bill) {
  if (!bill) return '';
  if (bill.frequency !== 'Custom') return String(bill.frequency || 'Monthly');
  const s = bill.customSchedule;
  if (!s) return 'Custom';
  if (s.type === CUSTOM_SCHEDULE_TYPES.NTH_WEEKDAY) {
    const nthLabel = NTH_LABELS[String(s.nth)] || NTH_LABELS[s.nth] || '';
    const dow = WEEKDAY_NAMES[Number(s.dayOfWeek)] || '';
    return nthLabel && dow ? `${nthLabel} ${dow} of month` : 'Custom';
  }
  if (s.type === CUSTOM_SCHEDULE_TYPES.EVERY_N_WEEKS) {
    const weeks = Math.max(1, Number(s.intervalWeeks) || 2);
    const dow = WEEKDAY_NAMES[Number(s.dayOfWeek)] || '';
    return dow ? `Every ${weeks} week${weeks === 1 ? '' : 's'} on ${dow}` : 'Custom';
  }
  return 'Custom';
}
